import type {KeyboardEvent, ReactElement} from 'react';
import {useRef} from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {MediaTypePreviewIcon} from './components/media-type-preview-icon/media-type-preview-icon';
import type {MediaTypeToggleOption, SocialMediaMediaTypeToggleProps} from './social-media-media-type-toggle.types';
import {MEDIA_TYPE_TOGGLE_KEYBOARD_KEYS, ROVING_TAB_INDEX_FOCUSABLE, ROVING_TAB_INDEX_UNFOCUSABLE} from './social-media-media-type-toggle.types';
import {getAdjacentOptionIndex, getNavigationDirectionForKey} from './social-media-media-type-toggle.library';
import styles from './social-media-media-type-toggle.module.scss';

export function SocialMediaMediaTypeToggle<TOptionId extends string>({
  options,
  selectedId,
  onSelect,
  isDisabled = false,
  disabledTooltipText = '',
  containerClassName = '',
}: SocialMediaMediaTypeToggleProps<TOptionId>): ReactElement {
  const optionElements = useRef<(HTMLDivElement | null)[]>([]);

  const onCardClick = (optionId: TOptionId): void => {
    if (isDisabled) {
      return;
    }

    onSelect(optionId);
  };

  const moveSelectionToOptionAtIndex = (optionIndex: number): void => {
    onSelect(options[optionIndex].id);
    optionElements.current[optionIndex]?.focus();
  };

  const onCardKeyDown = (keyboardEvent: KeyboardEvent<HTMLDivElement>, optionIndex: number): void => {
    if (isDisabled) {
      return;
    }

    if (keyboardEvent.key === MEDIA_TYPE_TOGGLE_KEYBOARD_KEYS.SPACE) {
      keyboardEvent.preventDefault();
      onSelect(options[optionIndex].id);
      return;
    }

    const direction = getNavigationDirectionForKey(keyboardEvent.key);

    if (direction === null) {
      return;
    }

    keyboardEvent.preventDefault();
    moveSelectionToOptionAtIndex(getAdjacentOptionIndex(optionIndex, direction, options.length));
  };

  const getTabIndexForOption = (option: MediaTypeToggleOption<TOptionId>, optionIndex: number): number => {
    const isAnyOptionSelected = options.some((candidate: MediaTypeToggleOption<TOptionId>): boolean => {
      return candidate.id === selectedId;
    });

    if (isAnyOptionSelected) {
      return option.id === selectedId ? ROVING_TAB_INDEX_FOCUSABLE : ROVING_TAB_INDEX_UNFOCUSABLE;
    }

    return optionIndex === 0 ? ROVING_TAB_INDEX_FOCUSABLE : ROVING_TAB_INDEX_UNFOCUSABLE;
  };

  const getCard = (option: MediaTypeToggleOption<TOptionId>, optionIndex: number): ReactElement => {
    const isSelected = option.id === selectedId;

    return (
      <ClickableDiv
        key={option.id}
        ref={(optionElement: HTMLDivElement | null): void => {
          optionElements.current[optionIndex] = optionElement;
        }}
        role="radio"
        aria-checked={isSelected}
        aria-disabled={isDisabled}
        tabIndex={getTabIndexForOption(option, optionIndex)}
        className={`${styles.mediaTypeCard} ${isSelected ? styles.mediaTypeCardSelected : ''}`}
        onKeyDown={(keyboardEvent: KeyboardEvent<HTMLDivElement>): void => {
          onCardKeyDown(keyboardEvent, optionIndex);
        }}
        onClick={(): void => {
          onCardClick(option.id);
        }}
      >
        <span className={styles.mediaTypeCardLead}>
          <span className={`${styles.radioDot} ${isSelected ? styles.radioDotSelected : ''}`} />
          <Text val={option.text} size={TextSize.SM} className={styles.mediaTypeCardLabel} bold />
        </span>
        <span className={`${styles.previewChip} ${isSelected ? styles.previewChipSelected : ''}`}>
          <MediaTypePreviewIcon type={option.previewIconType} className={styles.previewIcon} />
        </span>
      </ClickableDiv>
    );
  };

  const getToggle = (): ReactElement => {
    return (
      <div role="radiogroup" className={`${styles.mediaTypeToggleRow} ${isDisabled ? styles.mediaTypeToggleDisabled : ''} ${containerClassName}`}>
        {options.map(getCard)}
      </div>
    );
  };

  if (isDisabled && disabledTooltipText !== '') {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        delay={{show: 50, hide: 100}}
        overlay={
          <Tooltip id="media-type-toggle-tooltip" className="spacing-m-b-3 _pointer-events-none">
            {disabledTooltipText}
          </Tooltip>
        }
      >
        {getToggle()}
      </OverlayTrigger>
    );
  }

  return getToggle();
}
